import classNames from "classnames";
import { addHours, getHours, getMinutes, setHours, startOfDay, subHours } from "date-fns";
import format from "date-fns/format";
import isValid from "date-fns/isValid";
import parse from "date-fns/parse";
import setMinutes from "date-fns/setMinutes";
import IMask from "imask";
import React, { FC, useCallback, useMemo } from "react";
import { useComputedValue } from "../../../hooks/use-computed-value";
import { isSameTime } from "../../../utils/date-utils";
import { Input } from "../../input";
import { MaskedInput } from "../../input/masked-input";
import { RadioGroup } from "../../radio/radio-group";
import { Select, SelectHandler } from "../../select";
import { Text } from "../../text";
import { CSS_CLASSES } from "../date-time-input-css-classes";
import { HandOption, TimeOption, TimeProps } from "../date-time-input.types";

export const MERIDIANS: { value: "AM" | "PM" }[] = [{ value: "AM" }, { value: "PM" }];

export const MERIDIAN_TIME_MASK = {
  mask: "hh:mm am",
  lazy: true,
  blocks: {
    hh: {
      mask: IMask.MaskedRange,
      from: 1,
      to: 12,
      placeholderChar: "h",
    },
    mm: {
      mask: IMask.MaskedRange,
      from: 0,
      to: 59,
      placeholderChar: "m",
    },
    am: {
      mask: IMask.MaskedEnum,
      enum: ["AM", "PM", "am", "pm"],
    },
  },
};

export const TIME_MASK = {
  mask: "hh:mm",
  lazy: true,
  blocks: {
    hh: {
      mask: IMask.MaskedRange,
      from: 0,
      to: 23,
      placeholderChar: "h",
    },
    mm: {
      mask: IMask.MaskedRange,
      from: 1,
      to: 60,
      placeholderChar: "m",
    },
  },
};

export const TIME_FORMAT_24 = "HH:mm";

export const TIME_FORMAT_12 = "hh:mm a";

const formatMinute = (minute: string | number) => (+minute < 60 ? +minute : 0);

const formatHour = (clockMode: string) => (value: string) => {
  let hourNumber;
  if (value) {
    hourNumber = parseInt(value);
    const hourMod12 = hourNumber % 12;
    if (clockMode === "24-hour" && hourNumber > 24) {
      hourNumber = 23;
    } else if (clockMode === "12-hour" && !!hourMod12) {
      hourNumber = hourMod12;
    } else if (clockMode === "12-hour" && !hourMod12) {
      hourNumber = 12;
    }
  } else {
    hourNumber = value;
  }
  return hourNumber;
};

export const TimeInput: FC<TimeProps> = ({
  /* Time props */
  id,
  className,
  selectedTime,
  onTimeSelect,
  timePlaceholder = "12:00 AM",
  timeFormat,
  name,
  label,
  required,
  disabled,
  autoComplete,
  onFocus,
  onBlur,
  timePicker = false,
  hourPicker = false,
  minutePicker = false,
  clockMode = "12-hour",
  minuteInterval,
  hourInterval,
  minuteOptions,
  hourOptions,
  timeOptions,
  times,
  hourPlaceholder = "hh",
  hourLabel,
  minutePlaceholder = "mm",
  minuteLabel,
  showMinuteClearButton = false,
  showHourClearButton = false,
  showTimeClearButton = false,
  timeInputClassName,
  timePickerClassName,
  hourInputClassName,
  hourPickerClassName,
  minuteInputClassName,
  minutePickerClassName,
  meridianClassName,
  color = "interactive",
  /* useCalendar pass through props */
  minTime,
  maxTime,
  usePortal,
}) => {
  /*-------------------Start Options for select---------------- */
  const selectableTimeOptions: TimeOption[] = useMemo(() => {
    const timeFormat = clockMode === "12-hour" ? TIME_FORMAT_12 : TIME_FORMAT_24;
    let options: TimeOption[] = [];
    if (timeOptions) {
      const referenceDate = selectedTime ?? new Date();
      options = timeOptions.map(({ value }) => ({ value, time: parse(value, timeFormat, referenceDate) }));
    } else {
      options = times.map((time) => {
        const to: TimeOption = { time, value: format(time, timeFormat) };
        return to;
      });
    }

    return options;
  }, [clockMode, selectedTime, timeOptions, times]);

  const selectedOption = useMemo(() => {
    if (selectedTime) {
      return selectableTimeOptions.find((option) => isSameTime(option.time, selectedTime)) ?? null;
    }
    return null;
  }, [selectableTimeOptions, selectedTime]);

  const selectableHourOptions = useMemo(() => {
    let options: HandOption[] = [];
    if (hourOptions) {
      options = hourOptions;
    } else {
      if (!hourInterval) {
        return options;
      }
      let startOfHour;
      let endOfHour;
      if (clockMode === "12-hour") {
        startOfHour = minTime ? parseInt(format(minTime, "h")) : 1;
        endOfHour = maxTime ? parseInt(format(maxTime, "h")) : 12;
      } else {
        startOfHour = minTime ? parseInt(format(minTime, "H")) : 0;
        endOfHour = maxTime ? parseInt(format(maxTime, "H")) : 23;
      }
      while (startOfHour <= endOfHour) {
        options.push({ value: startOfHour.toString() });
        startOfHour += hourInterval;
      }
    }

    return options;
  }, [clockMode, hourInterval, hourOptions, maxTime, minTime]);

  const selectedHourOption = useMemo(() => {
    if (selectedTime) {
      const hour = format(selectedTime, clockMode === "12-hour" ? "h" : "HH");
      return selectableHourOptions.find(({ value }) => parseInt(hour) === parseInt(value)) ?? null;
    }
    return null;
  }, [clockMode, selectableHourOptions, selectedTime]);

  const selectableMinuteOptions = useMemo(() => {
    let options: HandOption[] = [];
    if (minuteOptions) {
      options = minuteOptions;
    } else {
      if (!minuteInterval) {
        return options;
      }
      let startOfHour = minTime ? parseInt(format(minTime, "m")) : 0;
      const endOfHour = maxTime ? parseInt(format(maxTime, "m")) : 59;
      while (startOfHour <= endOfHour) {
        options.push({ value: startOfHour.toString() });
        startOfHour += minuteInterval;
      }
    }

    return options;
  }, [maxTime, minTime, minuteInterval, minuteOptions]);

  const selectedMinuteOption = useMemo(() => {
    if (selectedTime) {
      const minute = getMinutes(selectedTime);
      return selectableMinuteOptions.find(({ value }) => minute === Number(value)) ?? null;
    }
    return null;
  }, [selectableMinuteOptions, selectedTime]);

  /*-------------------End Options for select---------------- */

  const selectedMeridian = selectedTime ? format(selectedTime, "a") : null;

  /*-------------------Start Handlers for select/Input change---------------- */

  const handleTimeEdit = useCallback(
    (e) => {
      try {
        const value = e.target.value;
        const timeFormat = clockMode === "12-hour" ? TIME_FORMAT_12 : TIME_FORMAT_24;
        const time = parse(value, timeFormat, new Date());
        if (isValid(time)) {
          onTimeSelect(time);
        } else {
          onTimeSelect(null);
        }
      } catch (e) {
        onTimeSelect(null);
      }
    },
    [clockMode, onTimeSelect]
  );

  const handleTimeSelect: SelectHandler<TimeOption> = useCallback(
    (option) => {
      if (option && !Array.isArray(option)) {
        onTimeSelect(option.time);
      } else {
        onTimeSelect(null);
      }
    },
    [onTimeSelect]
  );

  const handleHourEdit = useCallback(
    (value) => {
      let hourNumber;
      if (value) {
        hourNumber = parseInt(value);
        if (clockMode === "24-hour" && hourNumber > 24) {
          hourNumber = 23;
        }
        if (clockMode === "12-hour" && selectedTime) {
          const meridian = format(selectedTime, "a");
          if (meridian === "PM" && hourNumber < 12) {
            hourNumber += 12;
          }
          if (meridian === "AM" && hourNumber === 12) {
            hourNumber = 0;
          }
        }
      } else {
        hourNumber = 0;
      }
      const baseTime = selectedTime ? selectedTime : startOfDay(new Date());
      const newTime = setHours(baseTime, hourNumber);
      if (clockMode === "12-hour" && value === "") {
        onTimeSelect(null);
      } else {
        onTimeSelect(newTime);
      }
    },

    [clockMode, onTimeSelect, selectedTime]
  );

  const handleHourSelect: SelectHandler<HandOption> = useCallback(
    (option: HandOption) => {
      handleHourEdit(option?.value);
    },
    [handleHourEdit]
  );

  const extractedHour = useMemo(
    () => (selectedTime ? (clockMode === "12-hour" ? format(selectedTime, "h") : format(selectedTime, "H")) : ""),
    [clockMode, selectedTime]
  );

  const [hour, onHourChange, , onHourChangeWithFormatting] = useComputedValue(
    extractedHour,
    handleHourEdit,
    formatHour(clockMode)
  );

  const onBlurHourHandler = useCallback(
    (event) => {
      onHourChangeWithFormatting(event);
      onBlur?.(event);
    },
    [onBlur, onHourChangeWithFormatting]
  );

  const handleMinuteEdit = useCallback(
    (minute) => {
      const baseTime = selectedTime ? selectedTime : startOfDay(new Date());
      const minuteNumber = formatMinute(minute);
      const newTime = setMinutes(baseTime, minuteNumber);
      onTimeSelect(newTime);
    },
    [onTimeSelect, selectedTime]
  );

  const handleMinuteSelect: SelectHandler<HandOption> = useCallback(
    (option: HandOption) => {
      const baseTime = selectedTime ? selectedTime : startOfDay(new Date());
      const minutes = option ? parseInt(option.value) : 0;
      const newTime = setMinutes(baseTime, minutes);
      onTimeSelect(newTime);
    },
    [onTimeSelect, selectedTime]
  );

  const extractedMinutes = useMemo(() => (selectedTime ? format(selectedTime, "m") : ""), [selectedTime]);
  const [minute, onMinuteChange, , onMinuteChangeWithFormatting] = useComputedValue(
    extractedMinutes,
    handleMinuteEdit,
    formatMinute
  );

  const handleMeridianChange = useCallback(
    (meridian) => {
      const baseTime = selectedTime ? selectedTime : startOfDay(new Date());
      if (meridian === "PM" && getHours(baseTime) <= 12) {
        const newTime = addHours(baseTime, 12);
        onTimeSelect(newTime);
      }
      if (meridian === "AM" && getHours(baseTime) >= 12) {
        const newTime = subHours(baseTime, 12);
        onTimeSelect(newTime);
      }
    },
    [onTimeSelect, selectedTime]
  );

  const onBlurMinuteHandler = useCallback(
    (event) => {
      onMinuteChangeWithFormatting(event);
      onBlur?.(event);
    },
    [onBlur, onMinuteChangeWithFormatting]
  );

  /*-------------------End Handlers for select/Input change---------------- */

  const rootTimeClassName = classNames(CSS_CLASSES.DATE_TIME_INPUT_TIME, className);

  if (!timePicker && !hourPicker && !minutePicker) {
    /** Todo: onChange and value */
    let timeString: string = "";
    let maskOptions: any;
    if (clockMode === "12-hour") {
      maskOptions = MERIDIAN_TIME_MASK;
      if (selectedTime && isValid(selectedTime)) {
        timeString = format(selectedTime, TIME_FORMAT_12);
      }
    } else {
      maskOptions = TIME_MASK;
      if (selectedTime && isValid(selectedTime)) {
        timeString = format(selectedTime, TIME_FORMAT_24);
      }
    }

    return (
      <div className={rootTimeClassName}>
        <MaskedInput
          id={id}
          name="test"
          label={label ? label : ""}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          formField={false}
          placeholder={timePlaceholder}
          onFocus={onFocus}
          onBlur={handleTimeEdit}
          className={timeInputClassName}
          maskOptions={maskOptions}
          value={timeString}
          unmask={false}
          color={color}
        />
      </div>
    );
  }

  if (hourPicker || minutePicker) {
    let hourUI;
    let minuteUI;

    if (hourPicker) {
      hourUI = (
        <Select
          label={label ? label : ""}
          options={selectableHourOptions}
          selected={selectedHourOption}
          onSelect={handleHourSelect}
          formField={false}
          placeholder={hourPlaceholder}
          showClearButton={showHourClearButton}
          onFocus={onFocus}
          onBlur={onBlur}
          inputClassName={CSS_CLASSES.DATE_TIME_INPUT_HOURS_INPUT}
          className={hourPickerClassName}
          usePortal={usePortal}
          color={color}
        />
      );
    } else {
      hourUI = (
        <Input
          id={id}
          inputMode="numeric"
          placeholder={hourPlaceholder}
          label={hourLabel}
          value={hour}
          onChange={onHourChange}
          inputClassName={CSS_CLASSES.DATE_TIME_INPUT_HOURS_INPUT}
          className={hourInputClassName}
          maxLength={2}
          disabled={disabled}
          autoComplete={autoComplete}
          onFocus={onFocus}
          onBlur={onBlurHourHandler}
          color={color}
        />
      );
    }

    if (minutePicker) {
      minuteUI = (
        <Select
          id={id}
          label={label ? label : ""}
          options={selectableMinuteOptions}
          selected={selectedMinuteOption}
          onSelect={handleMinuteSelect}
          formField={false}
          placeholder={minutePlaceholder}
          showClearButton={showMinuteClearButton}
          onFocus={onFocus}
          onBlur={onBlur}
          inputClassName={CSS_CLASSES.DATE_TIME_INPUT_MINUTES_INPUT}
          className={minutePickerClassName}
          usePortal={usePortal}
          color={color}
        />
      );
    } else {
      minuteUI = (
        <Input
          inputMode="numeric"
          placeholder={minutePlaceholder}
          label={minuteLabel}
          value={minute}
          onChange={onMinuteChange}
          inputClassName={CSS_CLASSES.DATE_TIME_INPUT_MINUTES_INPUT}
          className={minuteInputClassName}
          maxLength={2}
          disabled={disabled}
          autoComplete={autoComplete}
          onFocus={onFocus}
          onBlur={onBlurMinuteHandler}
          color={color}
        />
      );
    }

    return (
      <div className={classNames(rootTimeClassName, "flex-wrap")}>
        <div className={CSS_CLASSES.DATE_TIME_INPUT_TIME_HOURS_MINUTES}>
          {hourUI}
          <Text variant="body-short" className={CSS_CLASSES.DATE_TIME_INPUT_DIVIDER}>
            :
          </Text>
          {minuteUI}
        </div>
        {clockMode === "12-hour" && (
          <RadioGroup
            label="select meridian"
            orientation="horizontal"
            radios={MERIDIANS}
            className={classNames(CSS_CLASSES.DATE_TIME_INPUT_MERIDIAN, meridianClassName)}
            value={selectedMeridian}
            onChange={handleMeridianChange as any}
            color={color}
          />
        )}
      </div>
    );
  }

  return (
    <div className={rootTimeClassName}>
      <Select
        id={id}
        label={label ? label : ""}
        options={selectableTimeOptions}
        selected={selectedOption}
        onSelect={handleTimeSelect}
        formField={false}
        placeholder={timePlaceholder}
        showClearButton={showTimeClearButton}
        onFocus={onFocus}
        onBlur={onBlur}
        className={timePickerClassName}
        usePortal={usePortal}
        color={color}
      />
    </div>
  );
};
